import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { Service } from 'webapp-types';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import FormDatetimePickerComponent from 'webapp/components/form/datetime-picker';
import FormSelectComponent from 'webapp/components/form/select';
import FormTextareaComponent from 'webapp/components/form/textarea';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type Annotation from 'webapp/models-custom/annotation';

interface Args {
	model: Annotation;
	submitInProgress: boolean;
	onSave: () => void;
	onCancel: () => void;
	showMetricChoice?: boolean;
	metricChoices?: LabelValuePairString[];
	isCheckboxEditDisabled?: boolean;
	onDelete?: () => void;
	onChangeMetric?: (uniqueId: string) => void;
	onSingleMetricOnlyChange?: (value: boolean) => void;
}

export default class AnnotationFormComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('constants') declare constantsService: Service.Constants;

	@tracked date: string;
	@tracked time: number;

	dateFormat: string;
	dateTimeFormat: string;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.dateFormat = this.accountService.accountDateFormat;
		this.dateTimeFormat = this.dateFormat + ' H';

		const momentObj = moment.unix(this.args.model.timestamp).tz(this.timezone);

		this.date = momentObj.format(this.dateFormat);
		this.time = momentObj.hours();
	}

	get timezone() {
		return this.args.model.timezone;
	}

	get timezoneLabel() {
		return (
			this.constantsService.timezones.find((item) => item.value === this.timezone)?.label ||
			''
		);
	}

	get showDelete() {
		return !!this.args.onDelete && !this.args.model.isNew;
	}

	get isSaveDisabled() {
		return !this.args.model.note?.trim();
	}

	get metricChoicesOrEmpty(): LabelValuePairString[] {
		return this.args.metricChoices || [];
	}

	@action
	changeDate(value: string) {
		this.date = value;
		this.updateTimestamp();
	}

	@action
	changeTime(value: number) {
		this.time = value;
		this.updateTimestamp();
	}

	@action
	changeSingleMetricOnly(value: boolean) {
		this.args.model.singleMetricOnly = value;
		this.args.onSingleMetricOnlyChange?.(value);
	}

	@action
	changeMetric(uniqueId: string) {
		this.args.onChangeMetric?.(uniqueId);
	}

	@action
	deleteAnnotation() {
		this.args.onDelete?.();
	}

	private updateTimestamp() {
		const dateTimeString = `${this.date} ${this.time}`;
		this.args.model.timestamp = moment
			.tz(dateTimeString, this.dateTimeFormat, this.timezone)
			.unix();
	}

	<template>
		<div class="annotation-form">
			<div class="annotation-form__datetime-row">
				<div class="annotation-form__date-field">
					<FormDatetimePickerComponent
						@dateFormat={{this.dateFormat}}
						@value={{this.date}}
						@disableTimePicker={{true}}
						@isDisabled={{@submitInProgress}}
						@onChange={{this.changeDate}}
					/>
				</div>
				<span class="annotation-form__at-label">at</span>
				<div class="annotation-form__time-field">
					<FormSelectComponent
						name="time"
						@selected={{this.time}}
						@options={{this.constantsService.hoursOfDay}}
						@isDisabled={{@submitInProgress}}
						@onChange={{this.changeTime}}
						@showFloatlabel={{false}}
					/>
				</div>
			</div>

			{{#if this.timezoneLabel}}
				<div class="annotation-form__timezone-hint">
					General data source timezone is set to:
					{{this.timezoneLabel}}
				</div>
			{{/if}}

			<div class="annotation-form__textarea">
				<FormTextareaComponent
					name="description"
					disabled={{@submitInProgress}}
					placeholder="Add a description"
					maxlength="256"
					@value={{@model.note}}
					@hideFloatLabel={{true}}
				/>
			</div>

			<div class="annotation-form__checkbox">
				<FormCheckboxComponent
					@name="single_metric_only"
					@selected={{@model.singleMetricOnly}}
					@isDisabled={{@isCheckboxEditDisabled}}
					@label="View on this metric only"
					@onChange={{this.changeSingleMetricOnly}}
					@item={{hash
						tooltip="The annotation will be saved and shown only for the specific metric."
						tooltipIcon="Question"
						tooltipMaxWidth=230
					}}
				/>
			</div>

			{{#if @showMetricChoice}}
				<div class="annotation-form__metric-choice">
					<FormSelectComponent
						name="metric"
						@selected=""
						@options={{this.metricChoicesOrEmpty}}
						@isDisabled={{@submitInProgress}}
						@isNumeric={{false}}
						@onChange={{this.changeMetric}}
						@showFloatlabel={{false}}
					/>
				</div>
			{{/if}}

			<div class="annotation-form__actions">
				<SubmitButtonComponent
					@text="Save"
					@extraClass="save"
					@onTrigger={{@onSave}}
					@isDisabled={{this.isSaveDisabled}}
					@submitInProgress={{@submitInProgress}}
				/>

				{{#unless @submitInProgress}}
					<a role="button" class="annotation-form__cancel" {{on "click" @onCancel}}>
						Cancel
					</a>
				{{/unless}}

				{{#if this.showDelete}}
					<button
						type="button"
						class="annotation-form__delete-btn"
						{{on "click" this.deleteAnnotation}}
					>
						<CommonUiIconComponent @name="Trash" @size="sm" />
						Delete
					</button>
				{{/if}}
			</div>
		</div>
	</template>
}
